import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ActionsService } from '@core/actions-modals/actions.service';
import { AuthService } from '@features/auth/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { TimeService } from '@services/common/time.service';
import { LogTimeWorkData } from '@shared/interfaces/sidepanel-create-item.enum';
import { ToasterService } from '@ui-kit/app-toast/toaster.service';
import { handleError } from '@ui-kit/utility/handleHttpErrorResponse';
import { CrudService } from 'collection';
import { Item, ItemModel } from 'collection';
import { addSeconds, format } from 'date-fns';
import { of, Subject } from 'rxjs';
import { catchError, take, takeUntil } from 'rxjs/operators';

import { legacyParse, convertTokens } from '@date-fns/upgrade/v2';

@Component({
  selector: 'work-detail-tooltip',
  templateUrl: './work-detail-tooltip.component.html',
  styleUrls: ['./work-detail-tooltip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkDetailTooltipComponent implements OnDestroy {
  @Input() showLogHoursBtn: boolean = true;
  @Input() showEdit: boolean = true;
  @Input() item;
  @Output() onEditClicked = new EventEmitter();
  @Output() onDeleteClicked = new EventEmitter();
  @Output() onMenuClosed = new EventEmitter();
  @Output() onLogTimeOpened = new EventEmitter<LogTimeWorkData>();
  @ViewChild(MatMenuTrigger, { static: true }) menuTrigger: MatMenuTrigger;

  workItem: ItemModel;
  lang: string;
  isAdmin: boolean;
  isManager: boolean;
  showLogHours: boolean;

  private _destroyed$ = new Subject();

  openMenu() {
    this.menuTrigger.openMenu();
  }

  closeMenu() {
    this.menuTrigger.closeMenu();
  }

  open() {
    this.openMenu();
    this.workItem.setParams('id', this.item.id);
    this.workItem
      .load()
      .pipe(
        catchError((err) => {
          const errorResponse = err.error;
          handleError(errorResponse, this._toastService);
          return of(null);
        })
      )
      .subscribe(() => {
        const lunchTimeConverted = this.workItem.data?.lunch_duration
          ? this._timeService.secondsToMinutes(this.workItem.data.lunch_duration)
          : null;
        this.workItem.data.lunchTimeConverted = lunchTimeConverted;
        this.workItem.data.mappedDuration = this.mapDuration(this.workItem.data.duration, this.workItem.data.from_time);
        this.showLogHours = this._auth.user?.id === this.workItem.data?.user_id;
      });
  }

  private mapDuration(duration: number, fromTime: string): string {
    const [hours, minutes, seconds] = fromTime.toString().split(':');
    const today = new Date();
    const dateTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), +hours, +minutes, +seconds);
    const result = addSeconds(legacyParse(dateTime), duration);

    return `${format(legacyParse(dateTime), convertTokens('HH:mm'))} - ${format(legacyParse(result), convertTokens('HH:mm'))}`;
  }

  click(e) {
    e.stopPropagation();
  }

  edit() {
    this.onEditClicked.emit(this.workItem.data);
    this.closeMenu();
  }

  delete() {
    this.closeMenu();

    const config = {
      title: marker('Delete work'),
      description: marker('Are you sure you want to delete Work?'),
      confirmButtonText: marker('Yes, Delete'),
      size: 'sm'
    };

    this._actionsService
      .confirm(config)
      .pipe(take(1))
      .subscribe(({ confirmed, modal }) => {
        if (confirmed) {
          this.workItem.deleteItem().subscribe(
            () => {
              modal.close();
              this._toastService.show({
                type: 'success',
                message: marker('Work was successfully deleted')
              });
              this.closeMenu();
              this.onDeleteClicked.emit(this.workItem.data);
            },
            (err) => {
              const errorResponse = err.error;
              handleError(errorResponse, this._toastService);
            }
          );
        }
      });
  }

  openLogTimeSidePanel(e: MouseEvent) {
    const cursorPosition = e.clientX;
    const { project, client, task, date, from_time, duration, lunch_duration } = this.workItem.data;
    this.onLogTimeOpened.emit({
      cursorPosition,
      project,
      client,
      task,
      date,
      from_time,
      duration,
      lunch_duration
    });
    this.closeMenu();
  }

  openMapPage(): void {
    const url = `https://www.google.com/maps/place/${this.workItem.data.project.project_location.lat},${this.workItem.data.project.project_location.lng}`;

    window.open(url, '_blank', 'noopener noreferer');
  }

  ngOnDestroy(): void {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  constructor(
    private _crud: CrudService,
    private _toastService: ToasterService,
    private _actionsService: ActionsService,
    private _translationService: TranslateService,
    private _auth: AuthService,
    private _timeService: TimeService,
    private _router: Router
  ) {
    this.workItem = new Item({
      api: _crud.createEntity({ name: 'work' }),
      params: {
        expand: 'project.project_location,client,task'
      }
    });

    this._translationService.onLangChange.pipe(takeUntil(this._destroyed$)).subscribe((data) => {
      this.lang = data?.lang;
    });

    this.isAdmin = this._auth.isAdmin();
    this.isManager = this._auth.isManager();
  }
}
